import { Either } from 'src/utils/either';

import { httpResponseHandler } from 'src/http/http-response-handler';

import {
  HttpTypes,
  BadRequestError,
  ConnectionError,
  UnexpectedError,
  AccessDeniedError,
  InternalServerError,
  TooManyRequestsError,
} from '../../http';
import { endpoints } from 'src/utils/axios';
import { AdminFilteredRewardsResponse, TrailActionBenefitListItem } from 'src/types/trails';
import {
  BenefitBulkUpdateInput,
  BenefitHighlightConfiguration,
  BenefitHighlightConfigurationPayload,
  BenefitHighlightContentSession,
} from 'src/types/benefits';

type BenefitsRepositoryError =
  | BadRequestError
  | UnexpectedError
  | InternalServerError
  | AccessDeniedError
  | ConnectionError
  | TooManyRequestsError;

type BenefitsRepositoryResponse<T> = Promise<Either<BenefitsRepositoryError, T | undefined>>;

export type AdminFilteredRewardsParams = {
  search?: string;
  page: number;
  pageSize: number;
};

export type TrailActionBenefitsParams = {
  search: string;
  filters: string;
  sort: string;
  page: number;
  pageSize: number;
  includeEventsToSearch: boolean;
  includeHighlightVisibility?: boolean;
};

export type TrailActionBenefitsApiResponse = {
  data: TrailActionBenefitListItem[];
  page: number;
  pageSize: number;
  total: number;
  totalFiltered: number;
  returnedCount?: number;
  totalPages: number;
};

export type BenefitBulkEditSession = {
  bulkSessionKey: string;
  totalItems: number;
};

export type BenefitBulkEditItemResult = {
  _id: string;
  benefitType: 'coupon' | 'product';
  status: 'queued' | 'success' | 'success_with_warning' | 'failed' | 'unknown';
  error?: { code: string; message: string } | null;
  warnings?: Array<{ code: string; message: string }>;
};

export type BenefitBulkEditProgress = {
  status: string;
  percentage: number;
  processedTotal: number;
  nextCursor?: string;
  batches: Array<{ batchKey: string; items: BenefitBulkEditItemResult[] }>;
};

export type BenefitBulkEditFeedback = {
  batches: Array<{ batchKey: string; items: BenefitBulkEditItemResult[] }>;
};

// Keep the temporary endpoint isolated here so the future dedicated route can be swapped once.
const TRAIL_ACTION_BENEFITS_ENDPOINT = endpoints.benefits.benefits.benefitsList;

export class BenefitsRepository {
  constructor(private api: HttpTypes.HttpClient) {}

  async getHighlightConfiguration(
    contentSession: BenefitHighlightContentSession
  ): BenefitsRepositoryResponse<BenefitHighlightConfiguration | null> {
    const response = await this.api.request({
      method: 'get',
      url: endpoints.benefits.benefits.highlightConfigurationById(contentSession),
    });
    return httpResponseHandler(response);
  }

  async saveHighlightConfiguration(
    payload: BenefitHighlightConfigurationPayload
  ): BenefitsRepositoryResponse<BenefitHighlightConfiguration> {
    const response = await this.api.request({
      method: 'put',
      url: endpoints.benefits.benefits.highlightConfiguration,
      body: payload,
    });
    return httpResponseHandler(response);
  }

  async getAdminFilteredRewards(
    params: AdminFilteredRewardsParams
  ): BenefitsRepositoryResponse<AdminFilteredRewardsResponse> {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.benefits.adminFilteredRewards,
      params,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getTrailActionBenefits(
    params: TrailActionBenefitsParams
  ): BenefitsRepositoryResponse<TrailActionBenefitsApiResponse> {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: TRAIL_ACTION_BENEFITS_ENDPOINT,
      params,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async exportBenefits(
    benefits: Array<{ id: string; type: 'coupon' | 'product' }>,
    userName: string,
    options?: { isAllSelected?: boolean }
  ): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'post',
      url: endpoints.benefits.benefits.exportData,
      body: {
        benefits,
        userName,
        isAllSelected: Boolean(options?.isAllSelected),
      },
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getBenefitsForNewUsers(): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: `benefits/active-new-user-benefits`,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getBenefitsList(params: {
    search: string;
    filters: string;
    sort: string;
    page: number;
    pageSize: number;
  }): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.benefits.benefitsList,
      params,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async getUserRedemptions(params: {
    userId: string;
  }): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'get',
      url: endpoints.benefits.benefits.userRedemptions,
      params,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async changeBenefitStatus(params: {
    benefitId: string;
    newStatus: 'active' | 'inactive';
  }): Promise<
    Either<
      | BadRequestError
      | UnexpectedError
      | InternalServerError
      | AccessDeniedError
      | ConnectionError
      | TooManyRequestsError,
      any | undefined
    >
  > {
    const request: HttpTypes.HttpRequest = {
      method: 'put',
      url: endpoints.benefits.benefits.changeStatus,
      params,
    };

    const response = await this.api.request(request);

    return httpResponseHandler(response);
  }

  async createBulkEditSession(
    clientIdempotencyKey: string,
    totalItems: number
  ): BenefitsRepositoryResponse<BenefitBulkEditSession> {
    const response = await this.api.request({
      method: 'post',
      url: endpoints.benefits.benefits.bulkEditSessions,
      body: { clientIdempotencyKey, totalItems },
    });

    return httpResponseHandler(response);
  }

  async submitBulkEditBatch(
    bulkSessionKey: string,
    batchKey: string,
    batchIndex: number,
    items: BenefitBulkUpdateInput[]
  ): BenefitsRepositoryResponse<unknown> {
    const response = await this.api.request({
      method: 'post',
      url: endpoints.benefits.benefits.bulkEditBatch(bulkSessionKey),
      body: { batchKey, batchIndex, items },
    });

    return httpResponseHandler(response);
  }

  async getBulkEditProgress(
    bulkSessionKey: string,
    updatedAfter?: string
  ): BenefitsRepositoryResponse<BenefitBulkEditProgress> {
    const response = await this.api.request({
      method: 'get',
      url: endpoints.benefits.benefits.bulkEditSession(bulkSessionKey),
      params: updatedAfter ? { updatedAfter } : undefined,
    });

    return httpResponseHandler(response);
  }

  async getBulkEditFeedback(
    bulkSessionKey: string
  ): BenefitsRepositoryResponse<BenefitBulkEditFeedback> {
    const response = await this.api.request({
      method: 'get',
      url: endpoints.benefits.benefits.bulkEditErrors(bulkSessionKey),
    });

    return httpResponseHandler(response);
  }
}
